import {
  ButtonMediumSecondaryLeadingIcon,
  Icon,
} from "@studio3t/generic-components";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { jobsApi, logsApi } from "@/api/client";
import type { LogStream } from "@/api/logStream";
import { ListPageHeader } from "@/components/ListPageHeader";
import type { LogEntry, LogLevel } from "@/types/api";
import { userStorage } from "@/utils/userStorage";
import { ALL_LOG_LEVELS, LogsFilterSidebar } from "./LogsFilterSidebar";
import {
  exportLogs,
  type LogStreamStatus,
  LogsHeaderBar,
  LogsTable,
} from "./LogsTable";
import {
  isNearBottom,
  nextFollowState,
  scrollViewportOf,
} from "./logAutoScroll";
import { entryKey, logStreamQuery } from "./logHistory";
import { phaseAfterError, type StreamPhase } from "./logStreamPhase";
import { useLogHistory } from "./useLogHistory";

/* The card splits into the filter sidebar and the log region (Figma
   AP_SystemLogs: AP_SystemLogs/filter as the left rail). No overflow
   clipping — the sidebar's collapse tab overlaps the card's bottom
   border, so the corner rounding sits on the inner surfaces instead. */
const Card = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  /* Fill the viewport: 122px = top bar (50) + page padding (10) + the
     sticky page header (52); the page's 10px bottom padding closes the
     gap under the card. */
  height: calc(100vh - 122px);
  /* Lets the header bar adapt to the card's width (entry count hides
     when the filter input would otherwise be squeezed out). */
  container: logscard / inline-size;
`;

/* The header spans the full card; sidebar and table share the row
   below it. */
const CardBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const LogRegion = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

// Per-user persisted UI state (the NewStudio explorer's userStorage
// recipe): defaults are stored as absence, so keys only exist while the
// user deviates from them.
const STORAGE_KEYS = {
  collapsed: "logs:filters-collapsed",
  levels: "logs:levels",
  filterText: "logs:filter-text",
  autoScroll: "logs:auto-scroll",
  job: "logs:job",
} as const;

function readStoredLevels(): ReadonlySet<LogLevel> {
  try {
    const raw = userStorage.getItem(STORAGE_KEYS.levels);
    if (!raw) return new Set(ALL_LOG_LEVELS);
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(ALL_LOG_LEVELS);
    return new Set(
      parsed.filter((level): level is LogLevel =>
        (ALL_LOG_LEVELS as readonly string[]).includes(level as string),
      ),
    );
  } catch {
    return new Set(ALL_LOG_LEVELS);
  }
}

// The live window: the tail appends and evicts the oldest past this.
// Older pages loaded from history are not subject to it — loading them
// pauses the tail, so nothing is appending while the reader is back
// there, and Resume starts the window over.
const MAX_LOGS = 10_000;
// Seen-key memory for dedup — allowed to outgrow the visible buffer so
// entries evicted by the cap stay recognized, rebuilt when it bloats.
const MAX_SEEN_KEYS = MAX_LOGS * 4;

// The stream carries every level — the sidebar's level checkboxes filter
// client-side, so toggling them never drops the connection. Only the job
// filter narrows the stream itself. Reconnects (job change, resume)
// clear the view and refetch the backfill.
/** Where the SSE connection is in its lifecycle: connecting = no socket
 *  yet this attempt, open = live, dropped = was open and died (the stream
 *  retrying), idle = intentionally closed. Defined with the rule that moves
 *  between them, in `logStreamPhase`. */

function openLogStream(
  jobFilter: string,
  handlers: {
    onOpen: () => void;
    onMessage: (data: string) => void;
    onError: (error: { status?: number }) => void;
  },
): LogStream {
  return logsApi.stream(logStreamQuery(jobFilter), handlers);
}

// The buffer behind the stream: dedup on the way in, then one state
// update per animation frame.
function useLogBuffer() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  // Keys of every entry accepted into the buffer (and recently evicted
  // ones) — arrivals matching a known key are re-deliveries and drop.
  const seenKeysRef = useRef<Set<string>>(new Set());
  // Accepted entries waiting for the per-frame flush: the backfill lands
  // as ~200 individual messages, so appends batch into one state update
  // per animation frame instead of one render per message.
  const pendingRef = useRef<LogEntry[]>([]);
  const flushFrameRef = useRef<number | null>(null);

  const cancelPendingFlush = useCallback(() => {
    if (flushFrameRef.current !== null) {
      cancelAnimationFrame(flushFrameRef.current);
      flushFrameRef.current = null;
    }
    pendingRef.current = [];
  }, []);

  const flushPending = useCallback(() => {
    flushFrameRef.current = null;
    const batch = pendingRef.current;
    pendingRef.current = [];
    setLogs((prev) => {
      const next = [...prev, ...batch].slice(-MAX_LOGS);
      // Rebuild the key memory from what's actually held once it
      // bloats past its allowance (idempotent under StrictMode's
      // double-invoked updaters).
      if (seenKeysRef.current.size > MAX_SEEN_KEYS) {
        seenKeysRef.current = new Set(next.map(entryKey));
      }
      return next;
    });
  }, []);

  const acceptEntry = useCallback(
    (entry: LogEntry) => {
      const key = entryKey(entry);
      if (seenKeysRef.current.has(key)) return;
      seenKeysRef.current.add(key);
      pendingRef.current.push(entry);
      if (flushFrameRef.current !== null) return;
      flushFrameRef.current = requestAnimationFrame(flushPending);
    },
    [flushPending],
  );

  const resetBuffer = useCallback(() => {
    setLogs([]);
    seenKeysRef.current = new Set();
  }, []);

  // A history page lands as the merged view, uncapped (see MAX_LOGS) and
  // straight to state — a page is one update, not a burst. The tail is
  // paused while pages load, so nothing else is writing — which is also
  // why the key memory stays as it is: no live arrival can collide with
  // a page before Resume clears both.
  const replaceEntries = useCallback((merged: LogEntry[]) => {
    setLogs(merged);
  }, []);

  return { logs, acceptEntry, cancelPendingFlush, resetBuffer, replaceEntries };
}

function useLogStream(jobFilter: string) {
  const [isStreaming, setIsStreaming] = useState(true);
  const [phase, setPhase] = useState<StreamPhase>("connecting");
  const streamRef = useRef<LogStream | null>(null);
  const { logs, acceptEntry, cancelPendingFlush, resetBuffer, replaceEntries } =
    useLogBuffer();

  const connectStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.close();
    }

    setPhase("connecting");

    streamRef.current = openLogStream(jobFilter, {
      onOpen: () => setPhase("open"),
      onMessage: (data) => {
        const entry = logsApi.parseLogEntry(data);
        if (!entry) return;
        acceptEntry(entry);
      },
      onError: (error) => setPhase((p) => phaseAfterError(p, error.status)),
    });
  }, [jobFilter, acceptEntry]);

  const disconnectStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.close();
      streamRef.current = null;
    }
    cancelPendingFlush();
    setPhase("idle");
  }, [cancelPendingFlush]);

  const toggleStreaming = useCallback(() => {
    if (isStreaming) {
      disconnectStream();
      setIsStreaming(false);
    } else {
      setIsStreaming(true);
      connectStream();
    }
  }, [isStreaming, connectStream, disconnectStream]);

  // Loading history parks the tail: the reader is back in time, and
  // live entries evicting the pages they are reading would be the cap
  // fighting them. Resume (the existing path) starts the live window
  // over.
  const pauseStreaming = useCallback(() => {
    disconnectStream();
    setIsStreaming(false);
  }, [disconnectStream]);

  // A job change opens that job's live window whether or not the tail
  // was paused: a paused view under a new job label would still show
  // the previous job's rows (and history browsing pauses the tail, so
  // this is the common case, not the corner).
  // biome-ignore lint/correctness/useExhaustiveDependencies: jobFilter is the trigger
  useEffect(() => {
    setIsStreaming(true);
  }, [jobFilter]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: jobFilter is an intentional trigger to reconnect the stream when the filter changes
  useEffect(() => {
    if (isStreaming) {
      resetBuffer();
      connectStream();
    }
    return () => disconnectStream();
  }, [isStreaming, jobFilter, connectStream, disconnectStream, resetBuffer]);

  return {
    logs,
    isStreaming,
    phase,
    toggleStreaming,
    pauseStreaming,
    replaceEntries,
  };
}

/** Debounces the display of transient stream states: connected/paused
 *  show immediately, but "connecting"/"disconnected" only take over
 *  after holding for the grace window — a fast resume goes straight
 *  paused→connected with no intermediate flash. */
function useSettledStatus(
  status: LogStreamStatus,
  graceMs = 400,
): LogStreamStatus {
  // Seeded with the real status: at mount the stream is connecting and
  // the skeletons say so, so the badge says so too. It used to seed
  // "connected" to spare a returning visitor a flash of "connecting" —
  // but the skeletons flash on every return anyway, and on a slow first
  // connect the badge promised a connection it did not have, then took
  // it back after the grace window. The grace applies to transitions
  // only, not to the truth at mount.
  const [displayed, setDisplayed] = useState<LogStreamStatus>(status);
  useEffect(() => {
    if (status === "connected" || status === "paused") {
      setDisplayed(status);
      return;
    }
    const timer = setTimeout(() => setDisplayed(status), graceMs);
    return () => clearTimeout(timer);
  }, [status, graceMs]);
  return displayed;
}

function useAllJobs(logs: LogEntry[]) {
  const [knownJobs, setKnownJobs] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    jobsApi
      .list()
      .then((jobs) => {
        if (mounted) setKnownJobs(jobs.map((j) => j.name).sort());
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(() => {
    const fromLogs = new Set<string>();
    for (const log of logs) {
      if (log.job_name) fromLogs.add(log.job_name);
    }
    return Array.from(new Set([...knownJobs, ...fromLogs])).sort();
  }, [logs, knownJobs]);
}

// Tail-follow, the old viewer's rule: scroll position decides — near the
// bottom follows new entries, away from it pauses. Two additions over
// the old page: the persisted preference gates the rule entirely (switch
// off = scrolling never engages anything), and the caller suppresses
// scroll events while the reveal gate is up (OverlayScrollbars' deferred
// init emits a reset scroll at position 0 that must not read as the user
// scrolling away). `autoScroll` is the live tail state the rule manages;
// only explicit toggles touch the preference (and storage).
function useAutoScroll(logs: LogEntry[], isStreaming: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const prefRef = useRef(
    userStorage.getItem(STORAGE_KEYS.autoScroll) !== "false",
  );
  const [autoScroll, setAutoScrollState] = useState(prefRef.current);
  // Mirror for the OverlayScrollbars event callback, which stays
  // identity-stable across renders.
  const autoScrollRef = useRef(autoScroll);
  autoScrollRef.current = autoScroll;

  const jumpToEnd = useCallback(() => {
    const viewport = scrollViewportOf(containerRef.current);
    if (!viewport) return;
    viewport.scrollTop = viewport.scrollHeight;
  }, []);

  // Pre-paint, so a backfill burst pins to the end instead of painting at
  // the old position and visibly chasing it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: logs is an intentional trigger to scroll on new log arrival
  useLayoutEffect(() => {
    if (autoScroll) jumpToEnd();
  }, [logs, autoScroll, jumpToEnd]);

  // OverlayScrollbars initializes deferred (idle callback) — under the
  // backfill burst that lands after every entry has rendered, with the
  // fresh viewport sitting at the top — and its update passes can reset
  // the scroll position (observed right after init). Re-pin on both its
  // `initialized` and `updated` events, or a quiet stream leaves the
  // list at the top until the next live entry. Readiness is also the
  // reveal gate's signal that rows can render already pinned.
  const [viewportReady, setViewportReady] = useState(false);
  const handleViewportReady = useCallback(() => {
    setViewportReady(true);
    if (autoScrollRef.current) jumpToEnd();
  }, [jumpToEnd]);

  // Mirror for the scroll handler, which stays identity-stable.
  const streamingRef = useRef(isStreaming);
  streamingRef.current = isStreaming;

  const handleScroll = useCallback(() => {
    // Switch off: inert — a manual scroll to the bottom must never turn
    // the tail on.
    if (!prefRef.current) return;
    const viewport = scrollViewportOf(containerRef.current);
    if (!viewport) return;
    const { scrollTop, scrollHeight, clientHeight } = viewport;
    const next = nextFollowState(
      isNearBottom(scrollTop, scrollHeight, clientHeight),
      streamingRef.current,
    );
    if (next !== null) setAutoScrollState(next);
  }, []);

  const setAutoScroll = useCallback(
    (on: boolean) => {
      prefRef.current = on;
      setAutoScrollState(on);
      if (on) jumpToEnd();
    },
    [jumpToEnd],
  );

  // History browsing takes the reader back in time, so the tail stops
  // following for now — like scrolling away does — without touching the
  // saved preference. Needed because a wheel up on a list too short to
  // scroll fires no scroll event, and a list that does not overflow
  // counts as "at the bottom": left engaged, the pin would override the
  // inserted page's scroll anchor as the rows measure in.
  const disengageAutoScroll = useCallback(() => setAutoScrollState(false), []);

  // The virtualized list grows as rows measure in; a tail that is
  // following stays pinned through it.
  const pinIfFollowing = useCallback(() => {
    if (autoScroll) jumpToEnd();
  }, [autoScroll, jumpToEnd]);

  return {
    containerRef,
    autoScroll,
    setAutoScroll,
    disengageAutoScroll,
    handleScroll,
    handleViewportReady,
    viewportReady,
    pinIfFollowing,
  };
}

// The sidebar's level checkboxes — one Set, defaulting to every level so
// nothing is hidden by surprise (the old "All Levels" default).
function useLevelSelection() {
  const [selectedLevels, setSelectedLevels] =
    useState<ReadonlySet<LogLevel>>(readStoredLevels);

  useEffect(() => {
    if (selectedLevels.size === ALL_LOG_LEVELS.length)
      userStorage.removeItem(STORAGE_KEYS.levels);
    else
      userStorage.setItem(
        STORAGE_KEYS.levels,
        JSON.stringify([...selectedLevels]),
      );
  }, [selectedLevels]);

  const toggleLevel = useCallback((level: LogLevel) => {
    setSelectedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const setAllLevels = useCallback((on: boolean) => {
    setSelectedLevels(new Set(on ? ALL_LOG_LEVELS : []));
  }, []);

  return { selectedLevels, toggleLevel, setAllLevels };
}

// The job filter lives in three places at once: the ?job= query param
// (deep-linkable), userStorage (sticky across visits) and the stream's
// own query.
function useJobFilter() {
  const [searchParams, setSearchParams] = useSearchParams();
  // A ?job= deep link wins; otherwise the last used job filter sticks.
  const initialJobFilter =
    searchParams.get("job") || userStorage.getItem(STORAGE_KEYS.job) || "all";

  const [jobFilter, setJobFilter] = useState<string>(initialJobFilter);

  const handleJobChange = useCallback(
    (v: string) => {
      setJobFilter(v);
      if (v === "all") userStorage.removeItem(STORAGE_KEYS.job);
      else userStorage.setItem(STORAGE_KEYS.job, v);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (v === "all") next.delete("job");
        else next.set("job", v);
        return next;
      });
    },
    [setSearchParams],
  );

  return { jobFilter, handleJobChange };
}

function usePersistedCollapsed() {
  const [filtersCollapsed, setFiltersCollapsed] = useState(
    () => userStorage.getItem(STORAGE_KEYS.collapsed) === "true",
  );

  useEffect(() => {
    if (filtersCollapsed) userStorage.setItem(STORAGE_KEYS.collapsed, "true");
    else userStorage.removeItem(STORAGE_KEYS.collapsed);
  }, [filtersCollapsed]);

  return [filtersCollapsed, setFiltersCollapsed] as const;
}

function usePersistedFilterText() {
  const [filterText, setFilterText] = useState(
    () => userStorage.getItem(STORAGE_KEYS.filterText) ?? "",
  );

  useEffect(() => {
    if (filterText) userStorage.setItem(STORAGE_KEYS.filterText, filterText);
    else userStorage.removeItem(STORAGE_KEYS.filterText);
  }, [filterText]);

  return [filterText, setFilterText] as const;
}

function streamStatusOf(
  isStreaming: boolean,
  phase: StreamPhase,
): LogStreamStatus {
  if (!isStreaming) return "paused";
  if (phase === "open") return "connected";
  if (phase === "dropped") return "disconnected";
  return "connecting";
}

// The header's text filter matches against message and job name.
function matchesFilterText(log: LogEntry, term: string): boolean {
  return (
    term === "" ||
    log.message.toLowerCase().includes(term) ||
    (log.job_name ?? "").toLowerCase().includes(term)
  );
}

// Level checkboxes and the header's text filter both narrow the list
// client-side.
function useVisibleLogs(
  logs: LogEntry[],
  selectedLevels: ReadonlySet<LogLevel>,
  filterText: string,
) {
  return useMemo(() => {
    const term = filterText.trim().toLowerCase();
    return logs.filter(
      (log) => selectedLevels.has(log.level) && matchesFilterText(log, term),
    );
  }, [logs, selectedLevels, filterText]);
}

// A connect attempt that cannot even produce response headers — a silent
// job stream held back by a buffering proxy (the Vite dev proxy forwards
// the head only with the first body bytes) — would hold skeletons forever.
// After this long the gate settles to the empty state and the "connecting"
// badge alone carries the story; logs arriving later still appear.
const CONNECT_PATIENCE_MS = 5000;

// Reveal once, settled: skeletons hold through connect + backfill +
// first pin, then the populated list appears already at the bottom —
// instead of empty bar → skeletons → rows at the top → jump. A grace
// window after the stream opens lets a system with no logs settle to
// its empty state (and backstops a viewport that never reports ready);
// a patience window on the connect itself backstops a stream that never
// opens.
/** True once the stream has sat in `target` for `ms` straight; resets the
 *  moment the phase moves. */
function usePhaseHeld(
  phase: StreamPhase,
  target: StreamPhase,
  ms: number,
): boolean {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (phase !== target) {
      setHeld(false);
      return;
    }
    const timer = setTimeout(() => setHeld(true), ms);
    return () => clearTimeout(timer);
  }, [phase, target, ms]);
  return held;
}

function useRevealGate(
  phase: StreamPhase,
  isStreaming: boolean,
  viewportReady: boolean,
  hasLogs: boolean,
): boolean {
  const openGraceElapsed = usePhaseHeld(phase, "open", 600);
  const connectPatienceElapsed = usePhaseHeld(
    phase,
    "connecting",
    CONNECT_PATIENCE_MS,
  );
  const settled =
    (viewportReady && hasLogs) || openGraceElapsed || connectPatienceElapsed;
  return isStreaming && !settled;
}

// The list's scroll and wheel gestures feed the tail-follow rule and
// history browsing — except while the reveal gate holds skeletons, when
// scroll events are the viewport being set up (the deferred-init reset
// lands at position 0), not the user.
function useListGestures(
  loading: boolean,
  handleScroll: () => void,
  history: { handleScroll: () => void; handleWheel: (deltaY: number) => void },
) {
  const handleUserScroll = useCallback(() => {
    if (loading) return;
    handleScroll();
    history.handleScroll();
  }, [loading, handleScroll, history.handleScroll]);

  const handleUserWheel = useCallback(
    (deltaY: number) => {
      if (!loading) history.handleWheel(deltaY);
    },
    [loading, history.handleWheel],
  );

  return { handleUserScroll, handleUserWheel };
}

function Logs() {
  const { jobFilter, handleJobChange } = useJobFilter();
  const [filtersCollapsed, setFiltersCollapsed] = usePersistedCollapsed();
  const [filterText, setFilterText] = usePersistedFilterText();
  const { selectedLevels, toggleLevel, setAllLevels } = useLevelSelection();

  const {
    logs,
    isStreaming,
    phase,
    toggleStreaming,
    pauseStreaming,
    replaceEntries,
  } = useLogStream(jobFilter);
  const streamStatus = useSettledStatus(streamStatusOf(isStreaming, phase));
  const allJobs = useAllJobs(logs);

  const visibleLogs = useVisibleLogs(logs, selectedLevels, filterText);
  const {
    containerRef,
    autoScroll,
    setAutoScroll,
    handleScroll,
    handleViewportReady,
    viewportReady,
    pinIfFollowing,
    disengageAutoScroll,
  } = useAutoScroll(visibleLogs, isStreaming);

  const history = useLogHistory({
    logs,
    jobFilter,
    isStreaming,
    containerRef,
    pauseStreaming,
    disengageAutoScroll,
    replaceEntries,
  });

  const loading = useRevealGate(
    phase,
    isStreaming,
    viewportReady,
    logs.length > 0,
  );

  const { handleUserScroll, handleUserWheel } = useListGestures(
    loading,
    handleScroll,
    history,
  );

  // Persist only EXPLICIT auto-scroll toggles (the sidebar switch) —
  // the scroll-position auto-disengage is transient and must not leak
  // into the next visit.
  const setAutoScrollPersisted = useCallback(
    (v: boolean) => {
      setAutoScroll(v);
      userStorage.setItem(STORAGE_KEYS.autoScroll, String(v));
    },
    [setAutoScroll],
  );

  return (
    <div>
      {/* House list-page header (the Pipelines pattern): H2 title with
          Export all on the title row (Figma Page/title 13017-11993).
          The design's "Clear all" is parked — the backend has no log
          deletion, and a local-only clear reads as more than it is. */}
      <ListPageHeader
        title="System logs"
        action={
          <ButtonMediumSecondaryLeadingIcon
            text="Export all"
            icon={Icon.Export}
            disabled={logs.length === 0}
            onClick={() => exportLogs(logs)}
          />
        }
      />

      <Card>
        <LogsHeaderBar
          isStreaming={isStreaming}
          status={streamStatus}
          onRun={() => {
            if (!isStreaming) toggleStreaming();
          }}
          onPause={() => {
            if (isStreaming) toggleStreaming();
          }}
          filterText={filterText}
          onFilterChange={setFilterText}
          entryCount={visibleLogs.length}
          loading={loading}
        />
        <CardBody>
          <LogsFilterSidebar
            autoScroll={autoScroll}
            setAutoScroll={setAutoScrollPersisted}
            jobFilter={jobFilter}
            onJobChange={handleJobChange}
            allJobs={allJobs}
            selectedLevels={selectedLevels}
            toggleLevel={toggleLevel}
            setAllLevels={setAllLevels}
            collapsed={filtersCollapsed}
            setCollapsed={setFiltersCollapsed}
          />
          <LogRegion>
            <LogsTable
              logs={visibleLogs}
              isStreaming={isStreaming}
              loading={loading}
              jobFilter={jobFilter}
              hasAnyLogs={logs.length > 0}
              historyStatus={history.status}
              logsContainerRef={containerRef}
              onScroll={handleUserScroll}
              onWheel={handleUserWheel}
              onViewportReady={handleViewportReady}
              onContentResize={pinIfFollowing}
              restoreAnchorRef={history.restoreAnchorRef}
            />
          </LogRegion>
        </CardBody>
      </Card>
    </div>
  );
}

export default Logs;
